import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AnyAction, Dispatch } from "redux";
import { State } from "reducers";
import { callApi } from "apiClient";
import { Org, OrgWithStats, ProtocolId } from 'shared/models';
import { setOrgs, setOrgsBusy, setOrgsError, updateOrg } from "actions/orgActions";
import { toError } from 'shared/util/toError';

//
// Helper function to wrap API calls to manage busy and error states consistently
//
const wrap = (fn: () => Promise<AnyAction>) => async (dispatch: Dispatch, getState: () => State): Promise<Org | null> => {
    if (getState().orgs.busy) {
        return null;
    }
    dispatch(setOrgsBusy(true));
    
    if (getState().orgs.error) {
        dispatch(setOrgsError(null));
    }

    return (async () => {
        try {
            const result = await fn();
            dispatch(result as AnyAction);
            return result.payload;
        }
        catch (e) {
            const err = toError(e);
            dispatch(setOrgsError(err));
            return null;
        }
        finally {
            dispatch(setOrgsBusy(false));
        }
    })();
};


const load = () =>
    wrap(async () => setOrgs(await callApi('GET', `/api/orgs`)));

const create = (data: Partial<Org>, protocolIds: ProtocolId[]) =>
    wrap(async () => {
        const org = await callApi('POST', '/api/orgs', data);
        await callApi('PUT', `/api/orgs/${org.id}/protocols`, protocolIds);
        return updateOrg(org);
    });

const update = (org: Org, protocolIds: ProtocolId[]) =>
    wrap(async () => {
        const updatedOrg = await callApi('PUT', `/api/orgs/${org.id}`, org);
        await callApi('PUT', `/api/orgs/${org.id}/protocols`, protocolIds);
        return updateOrg(updatedOrg);
    });


export function useOrgs() {
    const { t } = useTranslation();

    const {
        data: orgs,
        busy,
        error,
    } = useSelector((state: State) => state.orgs);
    
    const errorMessage = error && t([ `ORGS.ERROR.${error.message}`, `ERROR.${error.message}`, 'SERVER_ERROR' ]);

    const dispatch = useDispatch();
    
    const createOrg = useCallback((org: Org, protocolIds: ProtocolId[]) => (
        dispatch(create(org, protocolIds))
    ), [ dispatch ]);

    const updateOrg = useCallback((org: Org, protocolIds: ProtocolId[]) => (
        dispatch(update(org, protocolIds))
    ), [ dispatch ]);

    //
    // Load on the first usage of useOrgs
    //
    useEffect(() => {
        if (!error && !busy && !(orgs && Object.keys(orgs).length)) {
            dispatch(load());
        }
    }, [ orgs, busy, error, dispatch ]);

    return {
        busy,
        orgs,
        errorMessage,
        load,
        createOrg,
        updateOrg,
    };
}
